import { HubSpotFetchType } from '../../enums/hubspot/hubspot-fetch-type';
import {
	HubSpotAuthType,
	HubSpotConnectionDetails,
} from '../../interfaces/hubspot/hubspot-connection-details';
import { DestinationTaskConfigBase } from '../config-objects/destination-service-config-base';
import { IfRuleGroup } from '../if-gate/if-rule-group';

/**
 * Configuration for a HubspotGetAssociations task.
 * Retrieves HubSpot objects using the same service as HubSpotGetObjects.
 * fetchType determines the retrieval mode: page-based, ID-based, or filtered search.
 */
export class HubSpotGetAssociationsConfig
	extends DestinationTaskConfigBase
	implements HubSpotConnectionDetails
{
	/** When true, the access token is read from a flow variable. */
	useVariableToken?: boolean;
	/** Chooses between a static/variable Private App token vs a reusable Web App OAuth connection. @default HubSpotAuthType.PrivateAppToken */
	authType: HubSpotAuthType = HubSpotAuthType.PrivateAppToken;
	/** Selected `OAuthConnectionConfig.id` (provider=HubSpot). WebApp auth only. */
	connectionID?: string;
	/** HubSpot private app access token. Leave blank to use iHub credential store. @default '' */
	accessToken = '';
	/** HubSpot object type to retrieve (e.g. 'Contacts'). @default 'Contacts' */
	hubSpotObjectType = 'Contacts';
	/** Identifier property for GetAssociationsById mode. @default 'id' */
	idProperty = 'id';
	/** Expression or field path resolving to the FROM object HubSpot ID. Only used when fetchType=GetAssociationsById. */
	hubSpotObjectId?: string;
	/** Enables paginated retrieval. Use with pagingTokenVariable. @default false */
	fetchByPage?: boolean;
	/** Max records per page (1–100). @default 10 */
	limit: number | string = 10;
	/** Pagination cursor; usually managed by the pagingTokenVariable. */
	after?: string;
	/** Flow variable name storing the pagination cursor. @default 'HubSpotPageToken' */
	pagingTokenVariable?: string = 'HubSpotPageToken';
	/** HubSpot property API names to include in each returned object. */
	properties?: string[];
	/** Retrieval mode. GetAssociations=0 (page-based), GetAssociationsById=1 (by ID), WhereFilter=2 (search). @default HubSpotFetchType.GetAssociations */
	fetchType: HubSpotFetchType = HubSpotFetchType.GetAssociations;
	/** Filter rules for WhereFilter mode. */
	ruleGroup: IfRuleGroup = new IfRuleGroup();
	/** Additional association types to include in the response. */
	associations?: string[];
	/** When true, retrieves archived (deleted) objects. */
	archived?: boolean;
	/** WhereFilter only: when true, throws if no results found; when false, returns a "No Data" record. */
	emptyFetch?: boolean;
	/** Enables automatic retry on rate limit or transient errors. */
	enableRetries?: boolean;
	/** Maximum retry attempts. @default 3 */
	retryLimit = 3;
	/** Base delay in milliseconds for exponential backoff. @default 500 */
	retryDelay = 500;

	constructor(init?: Partial<HubSpotGetAssociationsConfig>) {
		super(init);
		if (init) {
			Object.assign(this, init);
		}
	}
}
